import { CSSProperties, FC, Fragment, KeyboardEvent, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./ChatQuiz.scss";
import { QuizField, QuizModel } from "../../../models/Chat/ChatMessageModel";
import { useChats } from "../../../providers/Chats/ChatsProvider";
import { BvIcon } from "bevi-icon";
import { AnimatePresence, motion } from "framer-motion";

// Transição entre etapas: desliza + fade conforme a direção (1 = avançar, -1 = voltar).
const stepVariants = {
  enter: (dir: number) => ({ x: dir >= 0 ? 26 : -26, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir: number) => ({ x: dir >= 0 ? -26 : 26, opacity: 0 }),
};

interface ChatQuizProps {
  quiz: QuizModel;
  /** Só a última pergunta do chat é interativa; o histórico fica em leitura. */
  active?: boolean;
  /** Quando o formulário já foi respondido, o resumo (markdown "**pergunta** resposta") substitui o form. */
  answersSummary?: string;
}

type Answer = { value: string; label: string };

const DATE_RE = /^\d{2}\/\d{2}\/\d{4}$/;

const maskDate = (raw: string): string => {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  if (digits.length > 4) return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  if (digits.length > 2) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return digits;
};

// AI-951/AJUSTES: formatação leve (markdown) no texto das perguntas vindas do
// backend — "\n" vira quebra de linha e *itálico*/**negrito** viram <em>/<strong>.
// Negrito é checado antes de itálico pra "**x**" não ser lido como "*" + "*x*" + "*".
const renderInlineMarkdown = (line: string): ReactNode[] => {
  const parts: ReactNode[] = [];
  const re = /\*\*(.+?)\*\*|\*(.+?)\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;
  while ((match = re.exec(line))) {
    if (match.index > lastIndex) parts.push(line.slice(lastIndex, match.index));
    if (match[1] !== undefined) parts.push(<strong key={key++}>{match[1]}</strong>);
    else parts.push(<em key={key++}>{match[2]}</em>);
    lastIndex = re.lastIndex;
  }
  if (lastIndex < line.length) parts.push(line.slice(lastIndex));
  return parts;
};

const renderQuestionText = (text?: string): ReactNode => {
  if (!text) return text;
  return text.split("\n").map((line, i) => (
    <Fragment key={i}>
      {i > 0 && <br />}
      {renderInlineMarkdown(line)}
    </Fragment>
  ));
};

// Rótulo pro RESUMO final (ChatQuizSummary): usa só a 1ª linha da pergunta —
// o hint/exemplo (2ª linha, em itálico) é só uma ajuda de preenchimento, não
// precisa reaparecer depois de respondida — e remove marcações de markdown
// residuais. O resumo tem seu próprio protocolo "**pergunta** resposta"
// (regex em ChatQuizSummary); deixar "*"/"\n" da pergunta vazar ali quebra
// esse parser (visto no AI-951/AJUSTES).
const summaryLabel = (question?: string): string =>
  (question ?? "").split("\n")[0].replace(/\*+/g, "").trim();

// ---------------------------------------------------------------------------
// Dispatcher: decide entre resumo respondido / leitura / wizard.
// Cada modo é um componente próprio (evita variação de quantidade de hooks).
// ---------------------------------------------------------------------------
const ChatQuiz: FC<ChatQuizProps> = ({ quiz, active = false, answersSummary }) => {
  if (answersSummary && answersSummary.trim()) {
    return <ChatQuizSummary summary={answersSummary} />;
  }
  if (!quiz?.fields?.length) return null;
  if (!active) return <ChatQuizReadonly fields={quiz.fields} />;
  return <ChatQuizWizard fields={quiz.fields} />;
};

export default ChatQuiz;

// ---------------------------------------------------------------------------
// Resumo respondido (no lugar do formulário)
// ---------------------------------------------------------------------------
const ChatQuizSummary: FC<{ summary: string }> = ({ summary }) => {
  const rows = summary
    .split(/\n\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      // [\s\S]+? (não ".+?") no grupo da pergunta: defesa extra caso algum "\n"
      // escape pro rótulo — "." sozinho não cruza quebra de linha em JS.
      const m = line.match(/^\*\*([\s\S]+?)\*\*\s*([\s\S]*)$/);
      return m ? { q: m[1].trim(), a: m[2].trim() } : { q: "", a: line };
    });

  return (
    <div className="ChatQuiz is-answered" data-testid="ChatQuiz">
      <div className="ChatQuiz__card">
        <ul className="ChatQuiz__summary">
          {rows.map((r, i) => (
            <li key={i}>
              {r.q && <span className="ChatQuiz__sq"><b>{r.q}</b></span>}
              <span className="ChatQuiz__sa">{r.a}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Leitura (histórico, sem ser respondido) — apenas as perguntas
// ---------------------------------------------------------------------------
const ChatQuizReadonly: FC<{ fields: QuizField[] }> = ({ fields }) => (
  <div className="ChatQuiz is-disabled" data-testid="ChatQuiz">
    <div className="ChatQuiz__card">
      <ul className="ChatQuiz__readonly">
        {fields.map((f) => (
          <li key={f.field}>{renderQuestionText(f.question)}</li>
        ))}
      </ul>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Wizard interativo (pergunta ativa)
// ---------------------------------------------------------------------------
const ChatQuizWizard: FC<{ fields: QuizField[] }> = ({ fields }) => {
  const { handleSend, isStreaming } = useChats();

  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  // Seleção múltipla por campo (AI-925, ex.: produtos): field -> valores marcados.
  const [multiValues, setMultiValues] = useState<Record<string, string[]>>({});
  const [stepIndex, setStepIndex] = useState(0);
  const [freeValue, setFreeValue] = useState("");
  const [focusIndex, setFocusIndex] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [direction, setDirection] = useState(1);
  // "Outro" ativo no passo atual (AI-936): troca o select por um input de texto livre.
  const [otherActive, setOtherActive] = useState(false);
  // AI-980: pop-up com o recorte do contracheque do slot atual (quando houver imagem).
  // Abre pra cima OU pra baixo (o lado com mais espaço), sempre deixando a pergunta
  // (o card) visível. Renderizado via portal e posicionado fixed a partir do botão/card.
  const [imageOpen, setImageOpen] = useState(false);
  const helpBtnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const questionRef = useRef<HTMLSpanElement>(null);
  const [popPos, setPopPos] = useState<
    {
      placement: "up" | "down";
      top?: number;
      bottom?: number;
      left: number;
      caret: number;
      imgMaxH: number;
      // AI-980: posição da pergunta p/ destacá-la (em branco) acima do backdrop.
      qTop: number;
      qLeft: number;
      qWidth: number;
    } | null
  >(null);

  // Conjunto de campos do form: usado para tratar como visível um campo cuja
  // dependência NÃO está no form (já foi resolvida no backend).
  const fieldKeys = useMemo(() => new Set(fields.map((f) => f.field)), [fields]);

  const isFieldVisible = (f: QuizField, ans: Record<string, Answer>): boolean => {
    const dep = f.depends_on;
    if (!dep) return true;
    if (!fieldKeys.has(dep.field)) return true; // dependência já resolvida no backend
    return ans[dep.field]?.value === dep.value;
  };

  const visible = useMemo(
    () => fields.filter((f) => isFieldVisible(f, answers)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fields, answers, fieldKeys]
  );
  const total = visible.length;
  const step = Math.min(stepIndex, Math.max(total - 1, 0));
  const current: QuizField | undefined = visible[step];
  // Campos opcionais (AI-934, ex.: UPAG) não bloqueiam o envio.
  const allAnswered = total > 0 && visible.every((f) => f.optional || !!answers[f.field]);
  const disabled = isStreaming || submitted;

  useEffect(() => {
    if (current?.input_type === "select") cardRef.current?.focus();
  }, [step, current?.input_type]);

  useEffect(() => {
    setFreeValue(current ? answers[current.field]?.value ?? "" : "");
    setFocusIndex(0);
    setOtherActive(false);
    setImageOpen(false); // AI-980: fecha o pop-up ao trocar de pergunta.
  }, [step, current?.field]); // eslint-disable-line react-hooks/exhaustive-deps

  // AI-980: fechar o pop-up do contracheque com ESC.
  useEffect(() => {
    if (!imageOpen) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") setImageOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [imageOpen]);

  // AI-980: posiciona o popover (fixed, no portal) escolhendo o lado com MAIS espaço.
  //  - pra BAIXO: abaixo do botão (a pergunta acima fica visível).
  //  - pra CIMA: acima do CARD inteiro (a pergunta abaixo fica visível — antes cobria).
  useEffect(() => {
    if (!imageOpen) {
      setPopPos(null);
      return;
    }
    const btn = helpBtnRef.current;
    const card = cardRef.current;
    if (btn && card) {
      const b = btn.getBoundingClientRect();
      const c = card.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const margin = 8;
      const gap = 8;
      const width = Math.min(vw * 0.94, 820);
      const left = Math.max(margin, Math.min(b.left, vw - width - margin));
      const caret = Math.max(14, Math.min(b.left + b.width / 2 - left, width - 26));
      const spaceBelow = vh - b.bottom - gap - margin; // abaixo do botão
      const spaceAbove = c.top - gap - margin; // acima do CARD (mantém a pergunta visível)
      const up = spaceAbove > spaceBelow;
      const imgMaxH = Math.max(160, Math.max(spaceBelow, spaceAbove) - 24); // menos padding
      const q = (questionRef.current ?? card).getBoundingClientRect();
      const qBox = { qTop: q.top, qLeft: q.left, qWidth: q.width };
      setPopPos(
        up
          ? { placement: "up", bottom: vh - c.top + gap, left, caret, imgMaxH, ...qBox }
          : { placement: "down", top: b.bottom + gap, left, caret, imgMaxH, ...qBox }
      );
    }
    const onResize = () => setImageOpen(false);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [imageOpen]);

  const advance = (nextAnswers: Record<string, Answer>) => {
    const nextVisible = fields.filter((f) => isFieldVisible(f, nextAnswers));
    setDirection(1);
    setStepIndex((i) => Math.min(i + 1, Math.max(nextVisible.length - 1, 0)));
  };

  // Confirma o texto ainda não "enviado" do campo livre atual: o usuário digitou no
  // input mas não apertou Enter nem o botão →. Sem isto, navegar entre perguntas ou
  // clicar em "Enviar" descartava o valor e o campo ia vazio. Selects não entram
  // (eles confirmam via choose).
  const commitCurrentFree = (base: Record<string, Answer>): Record<string, Answer> => {
    if (!current) return base;
    const isSelect =
      current.input_type === "select" && !!current.options && !(current.allow_other && otherActive);
    if (isSelect) return base;
    const v = freeValue.trim();
    if (!v) return base;
    if (current.input_type === "date" && !DATE_RE.test(v)) return base; // data inválida não confirma
    return { ...base, [current.field]: { value: v, label: v } };
  };

  const goPrev = () => {
    setAnswers((a) => commitCurrentFree(a));
    setDirection(-1);
    setStepIndex((i) => Math.max(i - 1, 0));
  };

  const goNext = () => {
    setAnswers((a) => commitCurrentFree(a));
    setDirection(1);
    setStepIndex((i) => Math.min(i + 1, total - 1));
  };

  const choose = (f: QuizField, value: string, label: string) => {
    if (disabled) return;
    const next = { ...answers, [f.field]: { value, label } };
    setAnswers(next);
    advance(next);
  };

  // Multi-seleção (AI-925): alterna a opção e mantém answers em sincronia (valores
  // e labels unidos por vírgula) para o resumo/envio. NÃO avança automaticamente —
  // o usuário marca quantas quiser e confirma no "Enviar".
  const isMulti = current?.input_type === "select" && !!current.multiple;

  const toggleMulti = (f: QuizField, value: string) => {
    if (disabled) return;
    const cur = multiValues[f.field] ?? [];
    const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
    setMultiValues({ ...multiValues, [f.field]: next });
    setAnswers((prev) => {
      const copy = { ...prev };
      if (next.length) {
        const labels = (f.options ?? [])
          .filter((o) => next.includes(o.value))
          .map((o) => o.label);
        copy[f.field] = { value: next.join(", "), label: labels.join(", ") };
      } else {
        delete copy[f.field];
      }
      return copy;
    });
  };

  // "Outro" (AI-936): select que também permite digitar um valor livre. Ao escolher
  // "Outro", troca a lista por um input de texto.
  const allowOther = current?.input_type === "select" && !!current.allow_other;
  const showOptions = current?.input_type === "select" && !!current.options && !(allowOther && otherActive);

  const isDate = current?.input_type === "date";
  const freeValid = !isDate || DATE_RE.test(freeValue);
  const canSubmitFree = !disabled && !!freeValue.trim() && freeValid;

  const submitFree = () => {
    if (!current || !canSubmitFree) return;
    choose(current, freeValue.trim(), freeValue.trim());
  };

  const submitAll = () => {
    if (disabled || !allAnswered) return;
    // Confirma primeiro o texto digitado no campo atual mas ainda não "enviado"
    // (Enter/→): sem isto, o último campo aberto (ex.: UPAG) ia vazio ao clicar
    // em "Enviar" mesmo com texto no input.
    const merged = commitCurrentFree(answers);
    // Campos respondidos entram com o valor; campos OPCIONAIS não respondidos
    // (ex.: UPAG pulada) entram com "-" para ficar explícito no resumo (AI-934).
    const summary = visible
      .filter((f) => merged[f.field] || f.optional)
      .map((f) => `**${summaryLabel(f.question)}** ${merged[f.field]?.label ?? "-"}`)
      .join("\n\n");
    setSubmitted(true);
    handleSend(summary);
  };

  // Pergunta opcional (AI-934, ex.: UPAG): "Pular" avança sem registrar resposta —
  // o campo é omitido no envio e o backend trata a ausência como "qualquer valor".
  // Se for o último passo, envia direto o que já foi respondido.
  const skipCurrent = () => {
    if (disabled || !current?.optional) return;
    if (step >= total - 1) submitAll();
    else goNext();
  };

  const onCardKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (disabled || !showOptions || !current) return;
    const opts = current.options ?? [];
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusIndex((i) => Math.min(i + 1, opts.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = opts[focusIndex];
      if (!opt) return;
      if (isMulti) toggleMulti(current, opt.value);
      else choose(current, opt.value, opt.label);
    }
  };

  const onFreeChange = (raw: string) => setFreeValue(isDate ? maskDate(raw) : raw);

  // AI-980: URL absoluta do recorte do contracheque (o back manda relativa /uploads/...).
  // REACT_APP_BEVIAJUDA_API termina com "/" (dev/homolog/prod), então removemos a barra
  // final da base para não gerar "//uploads" (que não casa o mount /uploads e dá 404).
  const apiBase = (process.env.REACT_APP_BEVIAJUDA_API ?? "").replace(/\/+$/, "");
  const referenceImageSrc = current?.reference_image_url
    ? `${apiBase}${current.reference_image_url}`
    : null;

  return (
    <div className="ChatQuiz" data-testid="ChatQuiz">
      <div
        className="ChatQuiz__card"
        ref={cardRef}
        tabIndex={current?.input_type === "select" ? 0 : -1}
        onKeyDown={onCardKeyDown}
      >
        <AnimatePresence mode="wait" custom={direction} initial={false}>
          <motion.div
            key={current?.field ?? "empty"}
            custom={direction}
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.18, ease: "easeOut" }}
          >
            <div className="ChatQuiz__top">
              <span className="ChatQuiz__question" ref={questionRef}>
                {renderQuestionText(current?.question)}
                {current?.optional && (
                  <span className="ChatQuiz__optional"> (opcional)</span>
                )}
                {referenceImageSrc && (
                  <>
                    <br />
                    <button
                      ref={helpBtnRef}
                      type="button"
                      className="ChatQuiz__helpImg"
                      onClick={() => setImageOpen((o) => !o)}
                      aria-label="Ver onde encontrar no contracheque"
                      aria-expanded={imageOpen}
                      title="Ver onde encontrar no contracheque"
                    >
                      <span>Ver onde encontrar</span>
                      <BvIcon name="search" variant="solid" size={0.9} />
                    </button>
                  </>
                )}
              </span>
              <div className="ChatQuiz__pager">
                <button
                  type="button"
                  className="ChatQuiz__nav"
                  onClick={goPrev}
                  disabled={step === 0}
                  aria-label="Anterior"
                >
                  ‹
                </button>
                <span className="ChatQuiz__count">{step + 1} de {total}</span>
                <button
                  type="button"
                  className="ChatQuiz__nav"
                  onClick={goNext}
                  disabled={step >= total - 1}
                  aria-label="Próxima"
                >
                  ›
                </button>
              </div>

              {/* AI-980: popover do contracheque via PORTAL (document.body) — escapa do
                  overflow do chat e do transform do framer-motion. Abre pro lado com mais
                  espaço (cima/baixo), sempre deixando a pergunta visível. Sem backdrop. */}
              {referenceImageSrc && imageOpen && popPos &&
                createPortal(
                  <>
                  <div
                    className="ChatQuiz__popoverBackdrop"
                    onClick={() => setImageOpen(false)}
                  />
                  {/* AI-980: cópia da pergunta em BRANCO, posicionada sobre a original,
                      acima do backdrop — mantém a pergunta destacada enquanto escurece o resto. */}
                  <div
                    className="ChatQuiz__popoverQuestion"
                    style={{ top: popPos.qTop - 2, left: popPos.qLeft - 6, maxWidth: popPos.qWidth + 12 }}
                    aria-hidden="true"
                  >
                    {renderQuestionText(current?.question)}
                    {current?.optional && <span> (opcional)</span>}
                  </div>
                  <div
                    className="ChatQuiz__popover"
                    ref={popRef}
                    data-placement={popPos.placement}
                    style={
                      {
                        top: popPos.top,
                        bottom: popPos.bottom,
                        left: popPos.left,
                        "--caret-left": `${popPos.caret}px`,
                      } as CSSProperties
                    }
                    role="dialog"
                    aria-label="Onde encontrar no contracheque"
                  >
                    <button
                      type="button"
                      className="ChatQuiz__popoverClose"
                      onClick={() => setImageOpen(false)}
                      aria-label="Fechar"
                    >
                      ×
                    </button>
                    <div className="ChatQuiz__popoverBody">
                      <img
                        className="ChatQuiz__popoverImg"
                        src={referenceImageSrc}
                        style={{ maxHeight: popPos.imgMaxH }}
                        alt={`Onde encontrar no contracheque: ${current?.question ?? ""}`}
                      />
                    </div>
                  </div>
                  </>,
                  document.body
                )}
            </div>

            {showOptions && current?.options ? (
              <ul className="ChatQuiz__options">
                {current.options.map((opt, i) => {
                  const selected = isMulti
                    ? (multiValues[current.field] ?? []).includes(opt.value)
                    : answers[current.field]?.value === opt.value;
                  return (
                    <li
                      key={opt.value}
                      className={`ChatQuiz__option ${i === focusIndex ? "is-focused" : ""} ${selected ? "is-selected" : ""}`}
                      onMouseEnter={() => setFocusIndex(i)}
                      onClick={() =>
                        isMulti
                          ? toggleMulti(current, opt.value)
                          : choose(current, opt.value, opt.label)
                      }
                    >
                      <span className="ChatQuiz__num">{i + 1}</span>
                      <span className="ChatQuiz__label">{opt.label}</span>
                      <>
                        {selected &&
                          <BvIcon name="checkCircle" variant="solid" width={24} height={24} color="#fff" />
                        }
                      </>
                    </li>
                  );
                })}
                {/* AI-936: opção "Outro" -> revela input de texto livre */}
                {allowOther && (
                  <li
                    key="__outro__"
                    className="ChatQuiz__option ChatQuiz__option--other"
                    onClick={() => setOtherActive(true)}
                  >
                    <span className="ChatQuiz__num">+</span>
                    <span className="ChatQuiz__label">Outro (digitar)</span>
                  </li>
                )}
              </ul>
            ) : (
              <div className="ChatQuiz__free">
                <input
                  className="ChatQuiz__input"
                  type={current?.input_type === "number" ? "number" : "text"}
                  inputMode={isDate ? "numeric" : undefined}
                  placeholder={isDate ? "DD/MM/AAAA" : "Digite sua resposta..."}
                  value={freeValue}
                  onChange={(e) => onFreeChange(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      submitFree();
                    }
                  }}
                />
                <button
                  type="button"
                  className="ChatQuiz__ok"
                  disabled={!canSubmitFree}
                  onClick={submitFree}
                >
                  <BvIcon name="arrowForward" variant="solid" width={24} height={24} />
                </button>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="ChatQuiz__footer">
          <span className="ChatQuiz__hint">
            {isMulti ? "↑↓ navegar · Enter marcar · escolha um ou mais" : "↑↓ navegar · Enter selecionar"}
          </span>
          <div className="d-flex align-items-center gap-2">
            {current?.optional && !disabled && !answers[current.field] && (
              <button
                type="button"
                className="ChatQuiz__skip py-1"
                onClick={skipCurrent}
              >
                Pular essa informação
              </button>
            )}
            {allAnswered && !disabled && step === total - 1 && (
              <button
                type="button"
                className="ChatQuiz__send py-1"
                onClick={submitAll}
              >
                <div className="d-flex align-items-center justify-content-between gap-1">
                  <div>Enviar</div>
                  <BvIcon
                    name="chevronRight"
                    size={1}
                    weight={600}
                  />
                </div>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
